import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import GlobalSearch from './GlobalSearch.jsx';
import DataHealth from './DataHealth.jsx';
import AccountPanel, { initials } from './AccountPanel.jsx';
import NotificationBell from './NotificationBell.jsx';
import { useAuth } from '../lib/auth.jsx';

/** Paths that belong to the List view tab (the state → county → ZIP tables). */
const isListPath = (p) => p === '/list' || /^\/[A-Za-z]{2}(\/|$)/.test(p);

/**
 * Round avatar bubble with the user's initials. Opens the account menu
 * (Profile / Teams / Sites / Settings, all collapsed) over the page —
 * including the map's filter panel — and closes on an outside click or
 * Escape. Each opening starts collapsed.
 */
function UserBubble() {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    // The panel is portaled to <body>, so "inside" means the bubble or the panel.
    const close = (e) => {
      if (ref.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const esc = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close, { passive: true });
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  if (!auth || auth.loading || !auth.available) return null;
  if (!auth.user) {
    return (
      <Link className="btn topbar-signin" to="/login">
        Sign in
      </Link>
    );
  }
  return (
    <div className="user-bubble-wrap" ref={ref}>
      <button
        type="button"
        className="user-bubble"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Account menu for ${auth.user.email}`}
        title={auth.user.email}
        onClick={() => setOpen((o) => !o)}
      >
        {initials(auth.user)}
      </button>
      {open &&
        createPortal(
          <div ref={panelRef} className="account-pop">
            <AccountPanel onDone={() => setOpen(false)} />
          </div>,
          document.body,
        )}
    </div>
  );
}

/**
 * Fixed top bar on every page: HeatWatch home button, Map / List view tabs,
 * the state-county-ZIP search, data-feed status and the account bubble.
 */
export default function TopBar() {
  const { pathname } = useLocation();
  const auth = useAuth();
  return (
    <header className="topbar">
      <Link to="/" className="topbar-brand" aria-label="HeatWatch home (map)">
        <span className="brand-mark" aria-hidden="true">◉</span>
        <span className="brand-name">HeatWatch</span>
      </Link>
      <nav className="topbar-tabs" aria-label="Views">
        <NavLink to="/" end className={({ isActive }) => `topbar-tab${isActive ? ' active' : ''}`}>
          Map
        </NavLink>
        <NavLink to="/list" className={() => `topbar-tab${isListPath(pathname) ? ' active' : ''}`}>
          List view
        </NavLink>
      </nav>
      <div className="topbar-search">
        <GlobalSearch />
      </div>
      <div className="topbar-right">
        <DataHealth />
        {auth?.user && <NotificationBell />}
        <UserBubble />
      </div>
    </header>
  );
}
